/**
 * Pure helpers for deciding what the recruiter's "Resume Score" cell should show for a candidate.
 *
 * Kept as a standalone, side-effect-free module (no React) so the decision logic can be unit
 * tested exhaustively without rendering the 3,900-line CandidatesTable god-component.
 *
 * Background: when a candidate uploads a bad/empty/unreadable resume, text extraction (LibreOffice
 * conversion + OpenAI fallback + OCR) yields nothing, so no AI score is generated. The server still
 * creates/updates the candidate with a viewable `resumeUrl`, a null `resumeScore`, and a
 * `resumeExtraction.method === 'failed'` marker (see server processPendingApplication). Historically
 * the recruiter table rendered a silent, non-clickable "—" for that null score — indistinguishable
 * from a candidate who never uploaded a resume at all. These helpers surface the failed state as an
 * openable "Failed" chip instead.
 */

/**
 * Did resume text extraction fail entirely for this candidate?
 *
 * True only when there is NO score (a later successful retry would populate one) AND the server
 * flagged the extraction as failed. Works with both the lite list payload (`resumeExtractionFailed`
 * boolean) and the full candidate document (`resumeExtraction.method === 'failed'`).
 *
 * @param {Object} candidate
 * @returns {boolean}
 */
export function isResumeExtractionFailed(candidate) {
  if (!candidate) return false;
  // A present score means extraction ultimately succeeded (e.g. an admin retry rescued it),
  // even if a stale `failed` marker lingers on the document.
  if (candidate.resumeScore != null) return false;
  if (candidate.resumeExtractionFailed === true) return true;
  if (candidate.resumeExtraction && candidate.resumeExtraction.method === 'failed') return true;
  return false;
}

/**
 * Decide the render state of the Resume Score cell. Mirrors the historical branch order in
 * CandidatesTable so behaviour for existing states is unchanged; adds the 'failed' state.
 *
 * States:
 *  - 'in_progress' : screening still running — score not available yet (dash).
 *  - 'dropped'     : candidate dropped/expired before uploading a resume — shows a 0% tooltip.
 *  - 'scored'      : a real resume score exists — clickable score chip.
 *  - 'failed'      : resume uploaded but unreadable — clickable "Failed" chip (NEW).
 *  - 'empty'       : nothing to show — plain dash.
 *
 * @param {Object} candidate
 * @returns {'in_progress'|'dropped'|'scored'|'failed'|'empty'}
 */
export function getResumeScoreCellState(candidate) {
  if (!candidate) return 'empty';
  if (candidate.status === 'In Progress') return 'in_progress';
  // Historical "dropped" signal: an explicit 0 with no backing score detail.
  if (candidate.resumeScore === 0 && !candidate.hasResumeScoreDetail) return 'dropped';
  if (candidate.resumeScore != null) return 'scored';
  if (isResumeExtractionFailed(candidate)) return 'failed';
  return 'empty';
}
